import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient } from '@src/api/apiClient/axiosClient';

import { Article, ArticleForm, ArticleFormError } from '../../types/article';
import { Pagination } from '../../types/pagination';

type ArticleIndexRequest = {
  keyword?: string;
  media_category_id?: number;
  article_tag_id?: number;
  status?: string;
  is_pickup?: boolean;
  page?: number;
  per_page?: number;
  sort?: string;
  direction?: string;
};

type ArticleIndexResponse = {
  data: Article[];
  pagination: Pagination;
};

type ArticleShowResponse = Article;

type ArticleCreateResponse = Article;
type ArticleCreateErrorResponse = ArticleFormError;

type ArticleUpdateResponse = Article;
type ArticleUpdateErrorResponse = ArticleCreateErrorResponse;

type ArticlePreviewResponse = {
  id: number;
  article_id: number;
  uuid: string;
};
type ArticlePreviewErrorResponse = ArticleCreateErrorResponse;

type ArticleCategoryBulkUpdateRequest = {
  article_ids: number[];
  media_category_id: number;
};

type UploadFileResponse = {
  url: string;
};
type UploadFileErrorResponse = {
  file: string[];
};

type DeleteFileResponse = unknown;
type DeleteFileErrorResponse = {
  message: string;
};

export const articleApiClient = {
  index: async (params: ArticleIndexRequest = {}): Promise<ApiResult<ArticleIndexResponse>> => {
    return await axiosClient.get('/tenant/articles', { params });
  },
  suggest: async (): Promise<ApiResult<Article[]>> => {
    return await axiosClient.get('/tenant/articles/suggest');
  },
  show: async (id: number): Promise<ApiResult<ArticleShowResponse>> => {
    return await axiosClient.get(`/tenant/articles/${id}`);
  },
  findByArticlePreviewUuid: async (uuid: string): Promise<ApiResult<ArticleShowResponse>> => {
    return await axiosClient.get(`/tenant/articles/preview/${uuid}`);
  },
  create: async (
    params: ArticleForm,
  ): Promise<ApiResult<ArticleCreateResponse, ArticleCreateErrorResponse>> => {
    return await axiosClient.post('/tenant/articles', params);
  },
  update: async (
    id: number,
    params: ArticleForm,
  ): Promise<ApiResult<ArticleUpdateResponse, ArticleUpdateErrorResponse>> => {
    return await axiosClient.put(`/tenant/articles/${id}`, params);
  },
  preview: async (
    id: number,
    params: ArticleForm,
  ): Promise<ApiResult<ArticlePreviewResponse, ArticlePreviewErrorResponse>> => {
    return await axiosClient.post(`/tenant/articles/${id}/preview`, params);
  },
  bulkUpdateCategory: async (params: ArticleCategoryBulkUpdateRequest): Promise<ApiResult> => {
    return await axiosClient.put('/tenant/articles/category', params);
  },
  delete: async (id: number): Promise<ApiResult> => {
    return await axiosClient.delete(`/tenant/articles/${id}`);
  },
  uploadThumbnail: async (
    file: File,
  ): Promise<ApiResult<UploadFileResponse, UploadFileErrorResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post('/tenant/articles/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadPdf: async (
    file: File,
  ): Promise<ApiResult<UploadFileResponse, UploadFileErrorResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post('/tenant/articles/upload/pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteFile: async (
    url: string,
  ): Promise<ApiResult<DeleteFileResponse, DeleteFileErrorResponse>> => {
    return await axiosClient.delete('/tenant/articles/s3delete', {
      params: {
        uploaded_image_url: url,
      },
    });
  },
};
